import { SeasonConfig } from '../types';
import { BackgroundTint } from './background-tint';
import { FallingSnow } from './falling-snow';
import WinterLogoDark from './images/logo-dark.svg';
import WinterLogoLight from './images/logo-light.svg';
import MorningLightMusic from './sounds/MorningLightMusic.mp3';

/**
 * Winter/Holiday Theme Configuration
 * Defines all assets, text, colors, and components for the winter seasonal theme
 */
export const winterConfig: SeasonConfig = {
	// Theme identification
	themeClassName: 'winter-theme',
	toggleEmoji: '❄️',

	// Logo assets
	logoLight: WinterLogoLight, // Used in navbar (light background)
	logoDark: WinterLogoDark, // Used in modal (dark background)

	// Modal assets
	modalSound: MorningLightMusic,
	modalSoundVolume: 0.1,

	// Primary theme color (festive red/green - using red as primary)
	primaryColor: {
		fg: '#dc2626', // red-600
		fgStrong: '#b91c1c', // red-700
		fgStronger: '#991b1b', // red-800
		fgStrongest: '#7f1d1d', // red-900
		bg: '#dc2626',
		bgHover: '#b91c1c',
		bgActive: '#991b1b',
		bgAlpha: 'rgba(220, 38, 38, 0.08)',
		bgAlphaHover: 'rgba(220, 38, 38, 0.16)',
		bgAlphaActive: 'rgba(220, 38, 38, 0.24)',
	},

	// Button text replacements - winter/holiday themed
	buttonTexts: ['❄️ Spread holiday cheer', '⛄ Send winter warmth', '🎁 Deliver appreciation', '✨ Share festive kudos', '🎅 Give a jolly shoutout'],

	// Modal decorative emojis - winter themed
	modalDecorations: [
		{ emoji: '🎄', top: '10px', left: '20px', fontSize: '40px', opacity: 0.2, rotation: -15 },
		{ emoji: '❄️', top: '30px', right: '30px', fontSize: '30px', opacity: 0.15, rotation: 15 },
		{ emoji: '⛄', bottom: '40px', left: '30px', fontSize: '25px', opacity: 0.15, rotation: -10 },
		{ emoji: '✨', top: '60px', left: '60%', fontSize: '20px', opacity: 0.1, rotation: 25 },
		{ emoji: '🎁', bottom: '60px', right: '40px', fontSize: '22px', opacity: 0.12, rotation: 12 },
	],

	// Modal colors - winter themed with blues and reds
	modalColors: {
		overlayBg: 'blackAlpha.800',
		contentBg: 'black',
		borderColor: 'red.500',
		boxShadow: '0 20px 60px rgba(220, 38, 38, 0.5)',
		logoDropShadowColor: 'rgba(220, 38, 38, 0.6)', // Red glow for winter/holiday
		subtitleColor: 'blue.200',
		enableButtonBg: 'red.600',
		enableButtonHoverBg: 'red.500',
		enableButtonActiveBg: 'red.700',
		enableButtonBoxShadow: '0 4px 15px rgba(220, 38, 38, 0.4)',
		enableButtonHoverBoxShadow: '0 8px 25px rgba(220, 38, 38, 0.6)',
		cancelButtonColor: 'blue.300',
		cancelButtonHoverColor: 'blue.200',
		noticeBg: 'blue.900',
		noticeBorderColor: 'blue.600',
		noticeTextColor: 'blue.200',
	},

	// Modal text content
	modalText: {
		subtitle: 'Celebrate the season with our festive winter theme! ❄️',
		features: [
			{
				emoji: '❄️',
				text: 'Gentle snowfall with beautiful snowflakes drifting across your screen',
			},
			{
				emoji: '🎄',
				text: 'Festive holiday colors with warm reds and cool blues throughout the interface',
			},
			{
				emoji: '🎁',
				text: 'Cheerful holiday-themed recognition messages to spread seasonal joy',
			},
		],
		endDateNotice: 'This festive theme will be available through the holiday season. Toggle it on/off anytime!',
		enableButtonText: 'Enable Winter Theme',
		cancelButtonText: 'Maybe Later',
	},

	// Visual effects components
	EffectsComponent: () => (
		<>
			<BackgroundTint />
			<FallingSnow />
		</>
	),
};
